import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { aboutFacts, experience, tricks } from '../data/portfolio';
import { fadeIn } from '../variants';
import bikePhoto from '../assets/demo/optimized/dirtbike-no-background-green.webp';
import eventSpacePlan from '../assets/demo/event-space-plan.svg';
import setupPhoto from '../assets/demo/optimized/setup-no-background-green.webp';

const tabs = [
  { id: 'tricks', label: 'Tricks' },
  { id: 'experience', label: 'Experience' },
  { id: 'mobile-setup', label: 'Mobile setup' },
  { id: 'event-space', label: 'Event Space' },
  { id: 'bike', label: 'Dirtbike' },
  { id: 'about', label: 'About me' },
];

const mobileSetupIntroCopy =
  'Full mobile FMX setup, easy to work with, made for events. This includes airbag setup, ramp delivery, on-site preparation and logistics. ' +
  'I provide the support needed to make the show work smoothly on location.';

const mobileSetupLogisticsCopy =
  '* For countries outside Europe, additional logistic actions may be required. There is possible to send an airbag with a bike, or to ride on a dirt landing prepared by the event organizer on the ramp built based on technical plans, which I can provide in further steps.';

const renderAboutValue = (fact) => {
  if (fact.label === 'Email') {
    return (
      <a
        href={`mailto:${fact.value}`}
        className='break-all underline transition hover:text-accent'
      >
        {fact.value}
      </a>
    );
  }

  if (fact.label === 'Experience') {
    return (
      <>
        15+ Years
        <br className='sm:hidden' />
        <span className='hidden sm:inline'> </span>
        riding
      </>
    );
  }

  if (fact.label === 'Languages') {
    return (
      <>
        Polish,
        <br className='sm:hidden' />
        <span className='hidden sm:inline'> </span>
        English
      </>
    );
  }

  return fact.value;
};

const Resume = ({ onSelectTrick, onSelectShow, onSelectVideo }) => {
  const [activeTab, setActiveTab] = useState('tricks');

  const heading = useMemo(() => {
    if (activeTab === 'tricks') {
      return {
        title: 'Tricks',
        copy: 'A list of tricks that can shape the show program.',
      };
    }

    if (activeTab === 'about') {
      return {
        title: 'About me',
        copy: 'Quick details about me for organizers planning a show.',
      };
    }

    if (activeTab === 'mobile-setup') {
      return {
        title: 'Mobile setup',
        copy: mobileSetupIntroCopy,
      };
    }

    if (activeTab === 'event-space') {
      return {
        title: 'Event Space',
        copy: 'Minimum event space is around 80 m total length with a clear riding lane, plus at least 10 m free height above the setup. Final placement depends on surface, barriers, and local safety rules.',
      };
    }

    if (activeTab === 'bike') {
      return {
        title: 'Dirtbike',
        copy: 'Brand new two-stroke Yamaha YZ250 2025 Monster Edition, FMX-prepped with jump-tuned suspension, flip levers, foot hook, steering stabilizer, grab-cut seat and plastics, wider footpegs, and dialed carburetor jetting.',
      };
    }

    return {
      title: 'Experience',
      copy: 'FMX and Motocross events I have already took part in.',
    };
  }, [activeTab]);

  return (
    <section id='about' className='page-section profile-section flex items-center'>
      <div className='section-shell'>
        <div className='grid gap-10 xl:grid-cols-[380px_1fr]'>
          <motion.aside
            variants={fadeIn('right', 0.1)}
            initial={false}
            whileInView='show'
            viewport={{ once: true, amount: 0.25 }}
          >
            <p className='section-kicker'>Rider profile</p>
            <h2 className='section-title mb-5'>Why me?</h2>
            <p className='section-copy mb-8'>
              I enjoy doing a great show while keeping strong contact with the audience.
              I bring tons of positive energy and know how to make people shout. :)
            </p>
            <div className='grid grid-cols-2 gap-3 xl:grid-cols-1'>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type='button'
                  onClick={() => setActiveTab(tab.id)}
                  className={`h-[54px] rounded-[8px] px-5 text-left text-sm font-semibold transition ${
                    activeTab === tab.id
                      ? 'bg-accent text-primary'
                      : 'bg-panel text-white hover:bg-surface hover:text-accent'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </motion.aside>

          <motion.div
            key={activeTab}
            variants={fadeIn('left', 0.12)}
            initial={false}
            animate='show'
            className='profile-tab-panel'
          >
            <h3 className='mb-4 text-[30px] font-bold leading-tight text-white md:text-[40px]'>
              {heading.title}
            </h3>
            <p
              className={`section-copy mb-8 max-w-none ${
                activeTab === 'mobile-setup' ? 'whitespace-pre-line' : ''
              }`}
            >
              {heading.copy}
            </p>

            {activeTab === 'experience' && (
              <div className='grid grid-cols-2 gap-4 sm:gap-5 xl:grid-cols-3'>
                {experience.map((item) => {
                  const content = (
                    <>
                      <p className='mb-4 text-sm font-semibold text-accent xl:mb-3'>
                        {item.years}
                      </p>
                      <h4 className='mb-3 text-xl font-semibold leading-snug text-white transition group-hover:text-accent group-focus-visible:text-accent xl:text-lg'>
                        {item.role}
                      </h4>
                      <p className='text-sm text-white/55'>{item.company}</p>
                    </>
                  );

                  if (item.targetTrickId || item.targetShowId || item.targetVideoId) {
                    return (
                      <button
                        key={`${item.years}-${item.role}`}
                        type='button'
                        onClick={() => {
                          if (item.targetTrickId) {
                            onSelectTrick?.(item.targetTrickId);
                            return;
                          }

                          if (item.targetVideoId) {
                            onSelectVideo?.(item.targetVideoId);
                            return;
                          }

                          onSelectShow?.(item.targetShowId);
                        }}
                        className='panel group w-full text-left transition hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary xl:p-5'
                        aria-label={`Show photo from ${item.role} in ${item.company}`}
                      >
                        {content}
                      </button>
                    );
                  }

                  return (
                    <article key={`${item.years}-${item.role}`} className='panel xl:p-5'>
                      {content}
                    </article>
                  );
                })}
              </div>
            )}

            {activeTab === 'tricks' && (
              <div className='grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4'>
                {tricks.map((trick) => {
                  const content = (
                    <span className='text-center text-base font-semibold leading-6 text-white transition group-hover:text-accent group-focus-visible:text-accent'>
                      {trick.name}
                    </span>
                  );

                  if (trick.targetTrickId) {
                    return (
                      <button
                        key={trick.name}
                        type='button'
                        onClick={() => onSelectTrick?.(trick.targetTrickId)}
                        className='panel group flex min-h-[104px] w-full items-center justify-center transition hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary'
                        aria-label={`Show ${trick.name} photo`}
                      >
                        {content}
                      </button>
                    );
                  }

                  return (
                    <div
                      key={trick.name}
                      className='panel flex min-h-[104px] items-center justify-center'
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === 'about' && (
              <dl className='grid grid-cols-2 gap-4 sm:gap-5'>
                {aboutFacts.map((fact) => (
                  <div key={fact.label} className='panel'>
                    <dt className='mb-3 text-sm text-white/45'>{fact.label}</dt>
                    <dd className='break-words text-base font-semibold leading-snug text-white sm:text-lg'>
                      {renderAboutValue(fact)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {activeTab === 'mobile-setup' && (
              <>
                <div className='panel h-[360px] overflow-hidden p-0 md:h-[520px]'>
                  <img
                    src={setupPhoto}
                    alt='Mobile setup'
                    className='h-full w-full bg-primary object-contain'
                  />
                </div>
                <p className='section-copy mt-6 max-w-none'>
                  {mobileSetupLogisticsCopy}
                </p>
              </>
            )}

            {activeTab === 'event-space' && (
              <div className='panel h-[360px] overflow-hidden p-0 md:h-[520px]'>
                <img
                  src={eventSpacePlan}
                  alt='FMX event space plan'
                  className='h-full w-full bg-primary object-contain'
                />
              </div>
            )}

            {activeTab === 'bike' && (
              <div className='panel h-[360px] overflow-hidden p-0 md:h-[520px]'>
                <img
                  src={bikePhoto}
                  alt='Yamaha YZ250 2025 Monster Edition'
                  className='h-full w-full bg-primary object-contain'
                />
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Resume;
